/**
 * Ksting
 *
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import {
  string,
  number,
  bool,
  array,
  object,
  func,
  oneOfType,
} from 'prop-types';
import classNames from 'clsx';

import BlackTieIcon from 'components/BlackTieIcon/BlackTieIcon';

import styles from './select.scss';

const renderOptions = (options = {}) =>
  Object.keys(options).map((key, index) => {
    if (typeof options[key] === 'object') {
      return renderOptions(options[key], true);
    }

    return (
      <option key={`${options[key]}-${index}`} value={key}>
        {options[key]}
      </option>
    );
  });

/**
 * This is the Select component.
 *
 * @author Chris Andersson <chris@wbisbi.com> and Pelle Lundgren <pelle.lundgren@vntrs.com>
 */
const Select = ({
  id,
  name,
  placeholder,
  value,
  required,
  readOnly,
  disabled,
  options,
  error,
  small,
  transparent,
  onFocus,
  onBlur,
  onChange,
}) => (
  <div className={styles.wrapper}>
    <div
      className={classNames(styles.select, {
        [styles.error]: error,
        [styles.small]: small,
        [styles.transparent]: transparent,
        [styles.readOnly]: readOnly,
        [styles.readOnly]: disabled,
        [styles.active]: !!value || !placeholder,
      })}
    >
      <select
        id={id || name}
        {...{
          name,
          value,
          required,
          disabled,
          readOnly,
          onFocus,
          onBlur,
          onChange,
        }}
      >
        {placeholder && (
          <option value="" disabled={required}>
            {placeholder}
          </option>
        )}
        {renderOptions(options)}
      </select>

      <span className={styles.arrow}>
        <BlackTieIcon icon="caret-up" small />
        <BlackTieIcon icon="caret-down" small />
      </span>
    </div>
  </div>
);

/**
 * Declare expected property types.
 *
 * @type {Object}
 */
Select.propTypes = {
  name: string.isRequired,
  id: string,
  placeholder: string,
  value: oneOfType([string, number]),
  required: bool,
  disabled: bool,
  readOnly: bool,
  compact: bool,
  options: oneOfType([array, object]).isRequired,
  error: bool,
  small: bool,
  transparent: bool,
  onFocus: func,
  onBlur: func,
  onChange: func,
};

/**
 * Set default properties.
 *
 * @type {Object}
 */
Select.defaultProps = {
  id: null,
  placeholder: null,
  value: '',
  required: false,
  disabled: false,
  readOnly: false,
  compact: false,
  error: false,
  small: false,
  transparent: false,
  onFocus: () => {},
  onBlur: () => {},
  onChange: () => {},
};

export default Select;
